import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getPrimaryVendorForProfile } from "./vendor-membership.server";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}


const createSchema = z.object({
  product_id: z.string().uuid(),
  email: z.string().trim().toLowerCase().email("Vnesite veljaven email naslov."),
  message: z.string().trim().min(5, "Sporočilo je prekratko.").max(2000),
});

export type InquiryRow = {
  id: string;
  product_id: string;
  vendor_id: string;
  profile_id: string;
  customer_email: string;
  customer_name: string | null;
  message: string;
  read_at: string | null;
  created_at: string;
  product?: { id: string; title: string; slug: string } | null;
  vendor?: { id: string; store_name: string; slug: string | null } | null;
};

export const createInquiry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => createSchema.parse(input))
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("id, email, first_name, last_name, display_name")
      .eq("auth_user_id", context.userId)
      .maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");

    const { data: product } = await supabaseAdmin
      .from("products")
      .select("id, title, vendor_id, vendor:vendors(id, store_name, profile:profiles(email, display_name, first_name))")
      .eq("id", data.product_id)
      .maybeSingle();
    if (!product || !product.vendor_id) throw new Error("Izdelek ne obstaja.");

    const customerName =
      profile.display_name ||
      [profile.first_name, profile.last_name].filter(Boolean).join(" ") ||
      null;

    const { data: inserted, error } = await supabaseAdmin
      .from("product_inquiries")
      .insert({
        product_id: product.id,
        vendor_id: product.vendor_id,
        profile_id: profile.id,
        customer_email: data.email,
        customer_name: customerName,
        message: data.message,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    // Add sender to marketing list "Poslana povpraševanja" (an email may belong to several labels)
    await supabaseAdmin
      .from("marketing_recipients")
      .upsert(
        { email: data.email, label: "Poslana povpraševanja", source: "inquiry" } as never,
        { onConflict: "email,label", ignoreDuplicates: true },
      );

    return { ok: true, id: inserted.id };
  });

export const getMyInquiries = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<InquiryRow[]> => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", context.userId).maybeSingle();
    if (!profile) return [];
    const { data } = await supabaseAdmin
      .from("product_inquiries")
      .select("id, product_id, vendor_id, profile_id, customer_email, customer_name, message, read_at, created_at, product:products(id, title, slug), vendor:vendors(id, store_name, slug)")
      .eq("profile_id", profile.id)
      .order("created_at", { ascending: false })
      .limit(100);
    return (data ?? []) as unknown as InquiryRow[];
  });

export const getVendorInquiries = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<InquiryRow[]> => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", context.userId).maybeSingle();
    if (!profile) return [];
    const vendor = await getPrimaryVendorForProfile(supabaseAdmin, profile.id);
    if (!vendor) return [];
    const { data } = await supabaseAdmin
      .from("product_inquiries")
      .select("id, product_id, vendor_id, profile_id, customer_email, customer_name, message, read_at, created_at, product:products(id, title, slug), vendor:vendors(id, store_name, slug)")
      .eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false })
      .limit(200);
    return (data ?? []) as unknown as InquiryRow[];
  });


export const getAllInquiries = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<InquiryRow[]> => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", context.userId).maybeSingle();
    if (!profile) return [];
    const { data: role } = await supabaseAdmin
      .from("user_roles").select("role").eq("profile_id", profile.id).eq("role", "admin").maybeSingle();
    if (!role) throw new Error("Dostop zavrnjen.");
    const { data } = await supabaseAdmin
      .from("product_inquiries")
      .select("id, product_id, vendor_id, profile_id, customer_email, customer_name, message, read_at, created_at, product:products(id, title, slug), vendor:vendors(id, store_name, slug)")
      .order("created_at", { ascending: false })
      .limit(500);
    return (data ?? []) as unknown as InquiryRow[];
  });
